import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Send, Paperclip, Building2, User, Filter, RefreshCw, Loader2, ArrowLeft, Mail, Calendar, FileText, Image as ImageIcon, FileSpreadsheet, FileArchive, FileType2, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (ct, name) => {
  const t = (ct || '').toLowerCase();
  const ext = (name || '').split('.').pop().toLowerCase();
  if (t.startsWith('image/')) return <ImageIcon className="w-3.5 h-3.5 text-emerald-600" />;
  if (t === 'application/pdf' || ext === 'pdf') return <FileType2 className="w-3.5 h-3.5 text-rose-600" />;
  if (t.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" />;
  if (t.includes('zip') || ext === 'zip') return <FileArchive className="w-3.5 h-3.5 text-amber-700" />;
  return <FileText className="w-3.5 h-3.5 text-ink-600" />;
};

const fmtFecha = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return s; }
};

export default function CorreosEnviados() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ entidad: '', contacto: '', desde: '', hasta: '', q: '' });
  const [verAdjuntos, setVerAdjuntos] = useState(null); // {subject, adjuntos, sent_at}

  const cargar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: '200' });
      if (filtros.entidad) params.set('entidad_id', filtros.entidad);
      if (filtros.contacto) params.set('contacto_id', filtros.contacto);
      if (filtros.desde) params.set('desde', filtros.desde);
      if (filtros.hasta) params.set('hasta', filtros.hasta);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/correo/enviados?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setItems(data.items || []);
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  // Búsqueda local por asunto/destinatario
  const filtrados = useMemo(() => {
    if (!filtros.q) return items;
    const qn = filtros.q.toLowerCase();
    return items.filter((m) => (
      (m.subject || '').toLowerCase().includes(qn)
      || (m.to_addresses || []).some((t) => t.toLowerCase().includes(qn))
      || (m.body_preview || '').toLowerCase().includes(qn)
    ));
  }, [items, filtros.q]);

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="correos-enviados-page">
      <div className="flex items-center justify-between mb-5 gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/correos')} data-testid="back-to-correos">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Bandeja
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-ink-900 flex items-center gap-2">
              <Send className="w-6 h-6 text-brand-600" /> Correos enviados
            </h1>
            <p className="text-sm text-ink-500">Últimos 200 correos enviados desde tus cuentas conectadas, con vinculación a CRM.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={async () => {
          try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({ limit: '5000' });
            if (filtros.entidad) params.set('entidad_id', filtros.entidad);
            if (filtros.contacto) params.set('contacto_id', filtros.contacto);
            if (filtros.desde) params.set('desde', filtros.desde);
            if (filtros.hasta) params.set('hasta', filtros.hasta);
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/correo/enviados/export-csv?${params}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `correos_enviados_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success('CSV descargado');
          } catch (e) { toast.error(`Export: ${e.message}`); }
        }} data-testid="enviados-export-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
        <Button size="sm" variant="outline" onClick={cargar} disabled={loading} data-testid="enviados-recargar">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
        </div>
      </div>

      <Card className="p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-ink-500" />
          <span className="text-sm font-semibold text-ink-700">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div>
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Buscar texto</label>
            <Input
              placeholder="asunto, destinatario, preview…"
              value={filtros.q}
              onChange={(e) => setFiltros({ ...filtros, q: e.target.value })}
              data-testid="enviados-filtro-q"
            />
          </div>
          <div>
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Entidad ID</label>
            <Input
              placeholder="opcional"
              value={filtros.entidad}
              onChange={(e) => setFiltros({ ...filtros, entidad: e.target.value })}
              data-testid="enviados-filtro-entidad"
            />
          </div>
          <div>
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Contacto ID</label>
            <Input
              placeholder="opcional"
              value={filtros.contacto}
              onChange={(e) => setFiltros({ ...filtros, contacto: e.target.value })}
              data-testid="enviados-filtro-contacto"
            />
          </div>
          <div>
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Desde</label>
            <Input
              type="date"
              value={filtros.desde}
              onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
              data-testid="enviados-filtro-desde"
            />
          </div>
          <div>
            <label className="text-[10px] text-ink-500 uppercase tracking-wider">Hasta</label>
            <Input
              type="date"
              value={filtros.hasta}
              onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
              data-testid="enviados-filtro-hasta"
            />
          </div>
        </div>
        <div className="flex justify-end mt-3 gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => { setFiltros({ entidad: '', contacto: '', desde: '', hasta: '', q: '' }); setTimeout(cargar, 100); }}
            data-testid="enviados-filtros-limpiar"
          >Limpiar</Button>
          <Button size="sm" onClick={cargar} data-testid="enviados-filtros-aplicar">Aplicar</Button>
        </div>
      </Card>

      {loading ? (
        <div className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-brand-600 mx-auto mb-2" />
          <p className="text-sm text-ink-500">Cargando…</p>
        </div>
      ) : filtrados.length === 0 ? (
        <Card className="p-12 text-center" data-testid="enviados-vacio">
          <Mail className="w-12 h-12 mx-auto text-ink-300 mb-3" />
          <p className="text-ink-600 mb-1">No hay correos enviados que coincidan.</p>
          <p className="text-xs text-ink-400">Los correos se persisten cuando los envías desde el composer en /correos.</p>
        </Card>
      ) : (
        <Card className="overflow-x-auto" data-testid="enviados-tabla">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500"><Calendar className="w-3 h-3 inline mr-1" />Enviado</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Asunto</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Destinatario</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Vinculado a</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Adjuntos</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((m, i) => (
                <tr
                  key={`${m.sent_at}-${i}`}
                  className="border-b border-ink-100 hover:bg-ink-50/40"
                  data-testid={`enviados-row-${i}`}
                >
                  <td className="px-3 py-2 text-xs text-ink-600 whitespace-nowrap">{fmtFecha(m.sent_at)}</td>
                  <td className="px-3 py-2 max-w-md">
                    <div className="font-medium text-ink-900 truncate">{m.subject || '(sin asunto)'}</div>
                    {m.body_preview && <div className="text-[11px] text-ink-500 truncate">{m.body_preview}</div>}
                    <div className="text-[10px] text-ink-400 mt-0.5">vía {m.via} · desde {m.from_address}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-700">
                    {(m.to_addresses || []).slice(0, 2).join(', ')}
                    {(m.to_addresses || []).length > 2 && <span className="text-ink-400"> +{m.to_addresses.length - 2}</span>}
                    {m.cc_addresses?.length > 0 && <div className="text-[10px] text-ink-400">CC: {m.cc_addresses.length}</div>}
                    {m.bcc_count > 0 && <div className="text-[10px] text-ink-400">CCO: {m.bcc_count}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {m.vinculado_entidad_id ? (
                      <button
                        onClick={() => navigate(`/clientes/${m.vinculado_entidad_id}`)}
                        className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                        data-testid={`enviados-link-entidad-${i}`}
                      >
                        <Building2 className="w-3 h-3" /> Empresa
                      </button>
                    ) : m.vinculado_contacto_id ? (
                      <span className="inline-flex items-center gap-1 text-ink-600">
                        <User className="w-3 h-3" /> Contacto
                      </span>
                    ) : (
                      <span className="text-ink-300 text-[10px] italic">externo</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {m.adjuntos_count > 0 ? (
                      <button
                        onClick={() => setVerAdjuntos(m)}
                        className="inline-flex items-center gap-1 text-amber-700 hover:underline"
                        data-testid={`enviados-ver-adjuntos-${i}`}
                      >
                        <Paperclip className="w-3 h-3" /> {m.adjuntos_count}
                      </button>
                    ) : (
                      <span className="text-ink-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {verAdjuntos && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setVerAdjuntos(null); }}
        >
          <Card className="max-w-lg w-full p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="enviados-dialog-adjuntos">
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-amber-700" /> Adjuntos del correo
            </h3>
            <div className="bg-ink-50 border border-ink-200 rounded p-2 mb-3 text-xs">
              <div className="font-medium text-ink-800 truncate">{verAdjuntos.subject || '(sin asunto)'}</div>
              <div className="text-ink-500">{fmtFecha(verAdjuntos.sent_at)}</div>
            </div>
            <ul className="space-y-2">
              {(verAdjuntos.adjuntos || []).map((a, i) => (
                <li
                  key={`${a.filename}-${i}`}
                  className="flex items-center gap-2 text-sm border border-ink-200 rounded px-3 py-2"
                  data-testid={`enviados-adjunto-${i}`}
                >
                  {iconFor(a.content_type, a.filename)}
                  <span className="flex-1 min-w-0 truncate">{a.filename}</span>
                  <Badge className="text-[10px] bg-ink-100 text-ink-600 border-0">{formatBytes(a.size || 0)}</Badge>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-ink-400 mt-3">
              Los bytes de los adjuntos no se almacenan en el CRM. Solo metadata (nombre, tipo y tamaño). El contenido vive en tu proveedor (Outlook/Gmail/IMAP).
            </p>
            <div className="flex justify-end mt-3">
              <Button variant="outline" size="sm" onClick={() => setVerAdjuntos(null)}>Cerrar</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
